import React, { useState } from 'react';
import { Plus, Edit, Trash2, LayoutGrid } from 'lucide-react';
import { useCrud } from '../../hooks/useApi';
import { institutionService } from '../../services/api';
import EmptyState from '../../components/Common/EmptyState';
import FormModalLayout from '../../components/Common/FormModalLayout';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { useTranslation } from 'react-i18next';

const Setores = () => {
  const { t } = useTranslation('institution');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ search: '', orgaoId: '' });

  const { useList: useOrgaosList } = useCrud('orgaos', institutionService.orgaoCrud);
  const { data: orgaosData } = useOrgaosList();
  const orgaos = Array.isArray(orgaosData) ? orgaosData : orgaosData?.data ?? [];

  const { useList, useCreate, useUpdate, useDelete } = useCrud('setores', institutionService.setorCrud);
  const { data, isLoading, error } = useList();
  const list = Array.isArray(data) ? data : data?.data ?? [];
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const filtered = list.filter((s) => {
    const matchSearch = !filters.search || (s.name && s.name.toLowerCase().includes(filters.search.toLowerCase()));
    const matchOrgao = !filters.orgaoId || s.orgao_id === filters.orgaoId;
    return matchSearch && matchOrgao;
  });

  const getOrgaoName = (id) => orgaos.find((o) => o.id === id)?.name || id;

  const handleSubmit = (formData) => {
    if (editing) updateMutation.mutate({ id: editing.id, data: formData });
    else createMutation.mutate(formData);
    setShowModal(false);
    setEditing(null);
  };

  if (error)
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
        </div>
      </FormPageShell>
    );

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("setoresTitle")}
          description="Setores vinculados a órgãos; utilizados em servidores e tramitação."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Instituição', to: '/institution/departments' },
            { label: 'Setores' },
          ]}
        >
          <Can permission={P.INSTITUTION_ACCESS}>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setShowModal(true);
              }}
              className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" /> Novo setor
            </button>
          </Can>
        </PageHeader>
      <div className="data-list-card p-5 sm:p-6">
        <div className="mb-4 flex gap-4 flex-wrap">
          <input type="text" placeholder={t('common:searchPlaceholder')} value={filters.search || ''} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="form-input max-w-xs" />
          <select value={filters.orgaoId || ''} onChange={(e) => setFilters({ ...filters, orgaoId: e.target.value })} className="form-select max-w-xs">
            <option value="">Todos os órgãos</option>
            {orgaos.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        {isLoading ? <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div> : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Órgão</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cód. SIIMM</th><th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th></tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{s.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{getOrgaoName(s.orgao_id)}</td>
                    <td className="px-6 py-4 text-sm">{s.codigo_siimm ?? '—'}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditing(s); setShowModal(true); }} className="text-primary-600 hover:text-primary-900 p-1 mr-2"><Edit className="w-4 h-4 inline" /></button>
                      <button onClick={() => window.confirm('Excluir?') && deleteMutation.mutate(s.id)} className="text-red-600 hover:text-red-900 p-1"><Trash2 className="w-4 h-4 inline" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <EmptyState
                icon={LayoutGrid}
                title={t("setoresEmpty")}
                description="Crie setores vinculados aos órgãos ou ajuste os filtros."
                action={
                  <Can permission={P.INSTITUTION_ACCESS}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setEditing(null);
                        setShowModal(true);
                      }}
                    >
                      Novo setor
                    </button>
                  </Can>
                }
              />
            )}
          </div>
        )}
      </div>
      </div>
      {showModal && (
        <FormModalLayout
          title={editing ? 'Editar setor' : 'Novo setor'}
          subtitle="Setores ficam vinculados a um órgão e podem ter código SIIMM."
          headerIcon={LayoutGrid}
          historyEntityType="departments"
          historyEntityId={editing?.id}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                }}
                className="btn btn-outline"
              >
                Cancelar
              </button>
              <Can permission={P.INSTITUTION_ACCESS}>
                <button type="submit" form="inst-setor-form" className="btn btn-primary">
                  Salvar
                </button>
              </Can>
            </>
          }
        >
          <form
            id="inst-setor-form"
            key={editing?.id ?? 'new'}
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              handleSubmit({
                name: form.setorName?.value || '',
                orgao_id: form.orgaoId?.value || '',
                receive_routing: form.receiveRouting?.checked || false,
                codigo_siimm: form.codigoSiimm?.value ? parseInt(form.codigoSiimm.value, 10) : undefined,
              });
            }}
            className="form-stack"
          >
            <div className="form-group">
              <label htmlFor="inst-setor-name" className="form-label form-label-required">
                Nome do setor
              </label>
              <input
                id="inst-setor-name"
                name="setorName"
                defaultValue={editing?.name}
                placeholder="Nome do setor"
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="inst-setor-orgao" className="form-label form-label-required">
                Órgão
              </label>
              <select id="inst-setor-orgao" name="orgaoId" defaultValue={editing?.orgao_id} className="form-select" required>
                <option value="">Selecione o órgão</option>
                {orgaos.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="inst-setor-siimm" className="form-label">
                Código SIIMM
              </label>
              <input
                id="inst-setor-siimm"
                name="codigoSiimm"
                type="number"
                defaultValue={editing?.codigo_siimm}
                placeholder="Opcional"
                className="form-input"
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                name="receiveRouting"
                type="checkbox"
                defaultChecked={editing?.receive_routing}
                className="form-check"
              />
              Receber tramitação
            </label>
          </form>
        </FormModalLayout>
      )}
    </FormPageShell>
  );
};

export default Setores;
